import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { PersonSummaryResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { formatDate } from '@tabita/i18n';
import { peopleApi, type ListPeopleParams } from '@/services/people.api';
import { Button, Spinner } from '@/components/ui';

/**
 * A clean, paginated roster — the (optionally filtered) current directory,
 * reached from the People list's "Print" menu, which passes its active
 * filters through as query params so this prints what the operator was
 * actually looking at. `GET /people` caps a page at 100 rows, so a full
 * roster is fetched by following `nextCursor` rather than one request.
 */
export function PrintDirectoryPage() {
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();
  const [people, setPeople] = useState<readonly PersonSummaryResponse[] | null>(null);

  const params: ListPeopleParams = {
    search: searchParams.get('search') || undefined,
    includeArchived: searchParams.get('includeArchived') === 'true',
    includeDeleted: searchParams.get('includeDeleted') === 'true',
    status: (searchParams.get('status') as ListPeopleParams['status']) || undefined,
    gender: searchParams.get('gender') || undefined,
    maritalStatus: searchParams.get('maritalStatus') || undefined,
    birthDateFrom: searchParams.get('birthDateFrom') || undefined,
    birthDateTo: searchParams.get('birthDateTo') || undefined,
  };
  // Stable across re-renders of the same URL, so the effect below does not
  // refetch on every render.
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setPeople(null);

    async function fetchAll(): Promise<void> {
      const all: PersonSummaryResponse[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await peopleApi.list({ ...params, cursor, limit: 100 });
        all.push(...page.data);
        if (!page.pagination.hasMore || !page.pagination.nextCursor) break;
        cursor = page.pagination.nextCursor;
      }
      if (!cancelled) setPeople(all);
    }

    void fetchAll();
    return () => {
      cancelled = true;
    };
    // `paramsKey` is the intentional dependency — `params` is a fresh
    // object every render.
  }, [paramsKey]);

  useEffect(() => {
    if (people && people.length > 0) window.print();
  }, [people]);

  return (
    <div className="print-page">
      <div className="print-page-toolbar">
        <Link to="/people" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Button type="button" variant="primary" onClick={() => window.print()}>
          {t('common.actions.print')}
        </Button>
      </div>

      <h1 className="print-page-title">{t('people.print.directoryTitle')}</h1>
      <p className="print-page-meta">
        {t('people.print.generatedOn', {
          // A pure display timestamp on a print page, not business logic —
          // `new Date()` bare is the one this repo's lint rule flags for
          // Clock injection; the explicit-argument form says the same
          // thing without asking a print page to take a Clock dependency.
          values: { date: formatDate(new Date(Date.now()), locale) },
        })}
        {people && ` · ${t('people.person.count', { count: people.length })}`}
      </p>

      {!people && <Spinner label={t('common.states.loading')} />}

      {people && (
        <table className="print-directory-table">
          <thead>
            <tr>
              <th scope="col">{t('people.person.singular')}</th>
              <th scope="col">{t('people.person.email')}</th>
              <th scope="col">{t('people.person.phone')}</th>
              <th scope="col">{t('common.labels.status')}</th>
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <tr key={person.id}>
                <td>{person.displayName}</td>
                <td>{person.primaryEmail ?? ''}</td>
                <td>{person.primaryPhone ?? ''}</td>
                <td>{t(`people.person.status.${person.status}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
